import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useLanguagePreference } from "../i18n/useLanguagePreference";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "../i18n";
import { ThemeSwitcher } from "../theme/ThemeSwitcher";
import { ComfortToggle } from "./ComfortToggle";
import { useServerVersion } from "../hooks/useServerVersion";
import { formatDisplayName } from "../utils/displayName";

/** Compact EN/ES toggle. Persists to localStorage + user.settings.language. */
function LanguageSwitcher() {
  const { t } = useTranslation();
  const { current, setLanguage, saving } = useLanguagePreference();

  return (
    <div className="lang-switch" role="group" aria-label={t("language.label")}>
      {SUPPORTED_LANGUAGES.map((lng) => (
        <button
          key={lng}
          type="button"
          disabled={saving}
          aria-pressed={current === lng}
          title={t(lng === "en" ? "language.english" : "language.spanish")}
          className={`lang-switch__btn ${current === lng ? "lang-switch__btn--active" : ""}`.trim()}
          onClick={() => {
            if (current !== lng) void setLanguage(lng as AppLanguage);
          }}
        >
          {t(`language.${lng}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * The navbar account menu: a dropdown under the user's (title-cased) display name that holds the
 * theme + language selectors, the running app version, and log out. Replaces the inline theme/lang
 * controls that used to sit loose in the top bar.
 */
export function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const version = useServerVersion();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = formatDisplayName(user) || t("nav.account");

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className={`location-switch__btn user-menu__btn ${open ? "location-switch__btn--open" : ""}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.account")}
        title={user?.email ?? undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="location-switch__name">{name}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="location-switch__menu user-menu__menu" role="menu">
          <div className="user-menu__section">
            <span className="user-menu__label">{t("theme.label")}</span>
            <ThemeSwitcher />
          </div>
          <div className="user-menu__section">
            <span className="user-menu__label">{t("language.label")}</span>
            <LanguageSwitcher />
          </div>
          <div className="user-menu__section">
            <span className="user-menu__label">{t("comfort.label")}</span>
            <ComfortToggle />
          </div>
          <button
            type="button"
            role="menuitem"
            className="location-switch__item user-menu__logout"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            {t("nav.logout")}
          </button>
          <div className="user-menu__version faint">
            <span>{version ? `v${version}` : "—"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
